import { actions } from '@app/store/action/transfer';

import * as TransferItemsHelper from '@app/helper/transfer-items';
import { TRANSFER_STATES } from '@app/transfer-bundle/constant/transfer-states';
import { TRANSFER_TYPES } from '@app/transfer-bundle/constant/transfer-types';
import { normalizeTransfer } from '@app/transfer-bundle/helpers/normalize-transfer';
import TransferItem from '@app/transfer-bundle/models/transfer-item';

function determineExpiry(
  userType,
  onChannel,
  defaultProExpiry,
  defaultFreeExpiry
) {
  if (userType === 'plus') {
    return 'never';
  }

  if (onChannel) {
    return defaultProExpiry;
  }

  return defaultFreeExpiry;
}

const findFile = (file_id) => (file) => file.id === file_id;

export default function (state = {}, action) {
  const { type } = action;
  switch (type) {
    case actions.TRANSFER_INIT: {
      // This resets the counter for iOS filenames
      TransferItem.resetFilenameCounter();
      const userType = action.userType || 'free';
      return {
        channel: action.channelName || null,
        domainUserId: action.domainUserId || null,
        expiry:
          action.expiry ||
          determineExpiry(
            userType,
            !!action.channelName,
            action.defaultProExpiry,
            action.defaultFreeExpiry
          ),
        files: action.files || [],
        id: action.id || false,
        items: action.items || [],
        itemsTree: action.itemsTree || TransferItemsHelper.buildItemsTree(),
        language: action.language || 'en',
        message: action.message || '',
        parent_id: action.parentId || false,
        password: action.password || null,
        recipients: action.recipients || [],
        sender: action.sender || '',
        shortURL: action.shortURL || null,
        state: action.state || TRANSFER_STATES.INIT,
        type: action.transferType || TRANSFER_TYPES.EMAIL,
        userType,
        uploadStartedAt: action.uploadStartedAt || 0,
        title: action.title,
        ...(action.spaceId ? { spaceId: action.spaceId } : {}),
        ...(action.secret ? { secret: action.secret } : {}),
        ...(action.recipientId ? { recipient_id: action.recipientId } : {}),
        ...(action.accountId ? { accountId: action.accountId } : {}),
        ...(action.expiryInSeconds
          ? { expiryInSeconds: action.expiryInSeconds }
          : {}),
      };
    }

    case actions.TRANSFER_CHANGE_TYPE:
      return { ...state, type: action.transferType };

    case actions.TRANSFER_CHANGE_EXPIRY:
      return { ...state, expiry: action.period };

    case actions.TRANSFER_CHANGE_PASSWORD:
      return { ...state, password: action.password };

    case actions.TRANSFER_SET_SENDER:
      return { ...state, sender: action.email };

    case actions.TRANSFER_SET_MESSAGE:
      return { ...state, message: action.message };

    case actions.TRANSFER_ADD_RECIPIENT: {
      return {
        ...state,
        // make recipients unique using a Set
        recipients: [...new Set([...state.recipients, ...action.emails])],
      };
    }

    case actions.TRANSFER_ADD_FILES: {
      // We use the transfer normalize to handle the
      // items/files/fileTree building, but omit other
      // properties to prevent clashing
      const { files, items, itemsTree, size } = normalizeTransfer({
        ...state,
        files: [...state.files, ...action.files],
      });

      return {
        ...state,
        files,
        items,
        itemsTree,
        size,
      };
    }

    case actions.TRANSFER_REMOVE_FILE: {
      // We use the transfer normalize to handle the
      // items/files/fileTree building, but omit other
      // properties to prevent clashing
      const { files, items, itemsTree, size } = normalizeTransfer({
        ...state,
        files: state.files.filter((file) => file.name !== action.fileName),
      });

      return {
        ...state,
        files,
        items,
        itemsTree,
        size,
      };
    }

    case actions.TRANSFER_REMOVE_DIRECTORY: {
      const directoryName = action.directoryName;
      const path = `${directoryName}/`;

      // We use the transfer normalize to handle the
      // items/files/fileTree building, but omit other
      // properties to prevent clashing
      const { files, items, itemsTree, size } = normalizeTransfer({
        ...state,
        files: state.files.filter(
          (file) => file.name !== directoryName && !file.name.startsWith(path)
        ),
      });

      return {
        ...state,
        files,
        items,
        itemsTree,
        size,
      };
    }

    case actions.TRANSFER_REMOVE_RECIPIENT:
      return {
        ...state,
        recipients: state.recipients.filter(
          (recipient) => recipient !== action.email
        ),
      };

    case actions.TRANSFER_RESET:
      return {
        ...state,
        id: false,
        state: TRANSFER_STATES.INIT,
      };

    case actions.TRANSFER_START:
      return state;

    case actions.TRANSFER_STATE_DOWNLOADABLE:
      return { ...state, state: TRANSFER_STATES.DOWNLOADABLE };

    case actions.TRANSFER_STATE_DOWNLOAD_STARTING:
      return { ...state, state: TRANSFER_STATES.DOWNLOADING };

    case actions.TRANSFER_STATE_FINISHED:
      return { ...state, state: TRANSFER_STATES.FINISHED };

    case actions.RECAPTCHA_CHALLENGE_RECEIVED:
      return {
        ...state,
        id: action.transfer_id,
        state: TRANSFER_STATES.RECAPTCHA,
      };

    case actions.UPLOAD_ERROR:
      if (
        ['LimitReached', 'UserBlocked', 'UnavailableParent'].includes(
          action.error
        )
      ) {
        return {
          ...state,
          lastError: action.error,
        };
      }
      return state;

    case actions.TRANSFER_STATE_DOWNLOAD_STARTING_FILE:
      state.items.find(
        (item) => item.id === action.file_id
      ).file.state.download = 'downloading';
      return state;

    case actions.TRANSFER_STATE_FINISHED_FILE:
      state.items.find(
        (item) => item.id === action.file_id
      ).file.state.download = 'done';
      return state;

    case actions.TRANSFER_RESET_DOWNLOAD:
      return { ...state, state: TRANSFER_STATES.DOWNLOADABLE };

    case actions.TRANSFER_DOWNLOAD_INVALID:
      return { ...state, state: TRANSFER_STATES.INVALID };

    case actions.TRANSFER_DOWNLOAD_EXPIRED:
      return { ...state, state: TRANSFER_STATES.EXPIRED };

    case actions.PREVIEW_LOADING: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = false;
      item.file.state.loaded = false;
      item.file.previewUri = action.file_url;

      return state;
    }

    case actions.PREVIEW_LOADED: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = false;
      item.file.state.loaded = true;

      return state;
    }

    case actions.UPLOAD_NEED_VERIFICATION:
      return { ...state, verification_required: true };

    case actions.UPLOAD_VERIFIED:
      return { ...state, verification_required: false };

    case actions.TRANSFER_RESET_VERIFICATION_REQUIRED:
      return { ...state, verification_required: undefined };

    case actions.SHOW_TRANSFER_OPTIONS_TOOLTIP:
      return { ...state, showTransferOptionsTooltip: true };

    case actions.PREVIEW_ERROR: {
      const itemIndex = state.items.findIndex(findFile(action.file_id));
      const item = state.items[itemIndex];

      item.file.state.error = true;
      item.file.state.loaded = false;
      item.file.previewUri = null;

      return state;
    }

    case actions.TRANSFER_SET_LANGUAGE:
      return { ...state, language: action.language };

    case actions.TRANSFER_SET_DOMAIN_USER_ID:
      return { ...state, domainUserId: action.domainUserId };

    case actions.TRANSFER_SET_CHANNEL:
      return { ...state, channel: action.channel };

    case actions.TRANSFER_SET_STATE:
      return { ...state, state: action.state };

    case actions.TRANSFER_SET_EXPIRY_IN_SECONDS: {
      if (typeof action.expiryInSeconds !== 'number') {
        return state;
      }
      return { ...state, expiryInSeconds: action.expiryInSeconds };
    }

    case actions.TRANSFER_SET_SHORT_URL:
      return { ...state, shortURL: action.shortURL };

    case actions.TRANSFER_SET_ID:
      return { ...state, id: action.id };

    case actions.TRANSFER_SET_DISPLAY_NAME:
      return { ...state, displayName: action.displayName };

    case actions.TRANSFER_SET_SECURITY_HASH:
      return { ...state, securityHash: action.securityHash };

    case actions.TRANSFER_SET_UPLOAD_STARTED_AT:
      return { ...state, uploadStartedAt: action.uploadStartedAt };
  }

  return state;
}
